/**
 * Container component for the intake form screen.
 *
 * Fetches the fully-built renderer URL from the backend (with scoped token)
 * and passes it to MainView for iframe/WebView rendering.
 * When voice dictation is enabled (VOICE_DICTATION flag), integrates
 * the recording/processing/injection flow. Fetches the Form.io form
 * definition dynamically to extract field schema for dictation.
 * @module features/player-intake/screens/Main/MainContainer
 */

import { useApolloClient, useReactiveVar } from "@apollo/client";
import {
  type Href,
  useLocalSearchParams,
  useNavigation,
  useRouter,
} from "expo-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform } from "react-native";
import { WebViewMessageEvent } from "react-native-webview";

import type { WebView } from "react-native-webview";

import { Box } from "@/components/atoms/Box";
import { VOICE_DICTATION } from "@/config/featureFlags";
import {
  buildInjectionScript,
  DictateButton,
  dictationProcessingMessageVar,
  dictationStatusVar,
  resetDictationState,
  useDictation,
} from "@/features/voice-report";
import { AnalyticsTracker } from "@/features/analytics";
import { usePlayerIntakeGetFormUrlQuery } from "@/generated/graphql";
import { useGetExpandedPlayerUrl } from "@/hooks/useExpandedPlayerUrl";
import { useToastNotification } from "@/hooks/useToastNotification";
import { useTheme } from "@/providers/ThemeProvider";
import { selectPlayerTab } from "@/stores/player";

import { useFormTokenRefresh } from "../../hooks/useFormTokenRefresh";
import { originOf } from "../../utils/originOf";
import {
  bridgeTokenRefreshAction,
  isTokenRefreshMessage,
  tokenRefreshRequestId,
} from "../../utils/tokenRefresh";
import MainView from "./MainView";

const LOG_PREFIX = "[ScoutReport:Intake]";

/**
 * Delay before routing away after a successful submit, so the renderer's
 * "Submission Created" confirmation screen is briefly visible (SE-5185 follow-up).
 */
const POST_SUBMIT_REDIRECT_MS = 1500;

/**
 * A submitted scout report is eventually consistent on the backend, so a single
 * refetch can race the write and return stale data. After the renderer signals
 * `report_submitted`, refetch PlayerDetailGetPlayer on an interval across a
 * bounded window so the Scouting Reports list and count refresh once the new
 * report is readable, instead of waiting on the player query's 30s poll or a
 * manual refresh (SE-5200). The poll is fire-and-forget: `router.replace`
 * unmounts this screen ~POST_SUBMIT_REDIRECT_MS later, and the refetches must
 * keep landing on the player page that replaces it, so it is driven off the
 * app-global Apollo client and self-terminates rather than being torn down on
 * unmount.
 */
const POST_SUBMIT_POLL_INTERVAL_MS = 5000;
const POST_SUBMIT_POLL_WINDOW_MS = 60000;

/**
 * Message type the Form.io renderer posts to its host on a successful submit
 * (see gemini-formio-renderer). Matched on web (iframe parent message) and,
 * on native, inside the bridge's relayed `formio_message` envelope.
 */
const REPORT_SUBMITTED_TYPE = "report_submitted";

/** Form.io types to exclude — layout containers, buttons, and system hidden fields */
const EXCLUDED_TYPES = new Set([
  "button",
  "hidden",
  "htmlelement",
  "content",
  "panel",
  "fieldset",
  "columns",
  "table",
  "tabs",
  "well",
  "container",
]);

/** A single Form.io component node */
interface FormioComponent {
  readonly key?: string;
  readonly type?: string;
  readonly label?: string;
  readonly values?: readonly { label: string; value: string }[];
  readonly data?: {
    readonly values?: readonly { label: string; value: string }[];
  };
  readonly validate?: { readonly min?: number; readonly max?: number };
  readonly components?: readonly FormioComponent[];
  readonly columns?: readonly { components?: readonly FormioComponent[] }[];
}

/** Extracted field definition for dictation */
interface FieldDef {
  readonly type: string;
  readonly label: string;
  readonly values?: readonly string[];
  readonly min?: number;
  readonly max?: number;
}

/**
 * Recursively extracts data-entry fields from Form.io component tree.
 * Skips layout containers (panels, columns, etc.) and non-data elements.
 * @param components - Form.io component array
 */
export const extractFormFields = (
  components: readonly FormioComponent[]
): Record<string, FieldDef> =>
  components.reduce(
    (acc, component) => {
      const nested = [
        ...(component.components ?? []),
        ...(component.columns?.flatMap(c => c.components ?? []) ?? []),
      ];

      const nestedFields = nested.length > 0 ? extractFormFields(nested) : {};

      const isDataField =
        component.key && component.type && !EXCLUDED_TYPES.has(component.type);

      const optionValues = component.values ?? component.data?.values ?? [];

      const fieldDef: FieldDef | undefined = isDataField
        ? {
            type: component.type as string,
            label: component.label ?? (component.key as string),
            ...(optionValues.length > 0
              ? { values: optionValues.map(v => v.label) }
              : {}),
            ...(component.validate?.min !== undefined
              ? { min: component.validate.min }
              : {}),
            ...(component.validate?.max !== undefined
              ? { max: component.validate.max }
              : {}),
          }
        : undefined;

      return {
        ...acc,
        ...nestedFields,
        ...(fieldDef ? { [component.key as string]: fieldDef } : {}),
      };
    },
    {} as Record<string, FieldDef>
  );

/**
 * Parses a WebView message event into a typed object.
 * @param event - The WebView message event
 */
export const parseWebViewMessage = (
  event: WebViewMessageEvent
): { type: string; payload?: unknown } | null => {
  try {
    return JSON.parse(event.nativeEvent.data) as {
      type: string;
      payload?: unknown;
    };
  } catch {
    return null;
  }
};

/**
 * Container for the intake form screen with optional voice dictation.
 */
const MainContainer = () => {
  // 1. VARIABLES & STATE
  const navigation = useNavigation();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const { playerId, matchId } = useLocalSearchParams<{
    playerId: string;
    matchId: string;
  }>();

  const isDictationEnabled = VOICE_DICTATION === "on";

  const matchIds = useMemo(
    () =>
      matchId?.split(",").filter(id => id && id !== "none" && id !== "0") ?? [],
    [matchId]
  );

  // Shared by the page query and the token refresh (SE-9160), so a refresh
  // mints a token for exactly the form the scout has open.
  const formUrlVariables = useMemo(
    () => ({
      playerId: playerId ?? "",
      matchIds: matchIds.length ? matchIds : undefined,
    }),
    [playerId, matchIds]
  );

  const { data, loading, error } = usePlayerIntakeGetFormUrlQuery({
    variables: formUrlVariables,
    skip: !playerId,
    fetchPolicy: "network-only",
  });

  const url = data?.getIntakeFormUrl ?? "";

  // Form.io field schema fetched from the form definition API
  const [fieldSchema, setFieldSchema] = useState<Record<
    string,
    unknown
  > | null>(null);
  const formFetchedRef = useRef(false);
  const hasLoggedIntakeStartRef = useRef(false);

  // Dictation state
  const dictationStatus = useReactiveVar(dictationStatusVar);
  const processingMessage = useReactiveVar(dictationProcessingMessageVar);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const webViewRef = useRef<WebView | null>(null);

  // Post-submit routing (SE-5185 follow-up)
  const router = useRouter();
  const getExpandedPlayerUrl = useGetExpandedPlayerUrl();
  const toast = useToastNotification();
  const client = useApolloClient();
  const { answerOnWeb, answerOnNative } = useFormTokenRefresh({
    variables: formUrlVariables,
    iframeRef,
    webViewRef,
  });
  // Guards a one-shot redirect against duplicate submit events and a timer that
  // could otherwise fire after the screen has unmounted.
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasNavigatedRef = useRef(false);

  /**
   * On a successful report submit — signalled by the renderer via a
   * `report_submitted` postMessage — route the user to the player's Reports &
   * Notes tab (where the new report appears) instead of stranding them on the
   * form, whose only exit returns to match selection. A short delay lets the
   * renderer's "Submission Created" confirmation show first; `router.replace`
   * drops the matches/intake screens from history so back doesn't return here.
   */
  const handleReportSubmitted = useCallback(() => {
    if (hasNavigatedRef.current || !playerId) return;
    hasNavigatedRef.current = true;
    console.info(LOG_PREFIX, "Report submitted — routing to player page");
    // SE-8285: `playerId` is the player we are about to route to — the guard
    // above has already returned when it is missing.
    selectPlayerTab(playerId, "reportsNotes");
    // Fire-and-forget poll across the eventual-consistency window so the new
    // report surfaces on the player page without a manual refresh (SE-5200).
    // Intentionally not torn down on unmount — this screen unmounts when the
    // redirect below fires, and the refetches must keep landing on the player
    // page that replaces it. The interval clears itself after the window.
    const pollStartedAt = Date.now();
    const poll = setInterval(() => {
      if (Date.now() - pollStartedAt >= POST_SUBMIT_POLL_WINDOW_MS) {
        clearInterval(poll);
        return;
      }
      void client
        .refetchQueries({ include: ["PlayerDetailGetPlayer"] })
        .catch(error => {
          console.warn(
            LOG_PREFIX,
            "Failed to refetch player data after report submit",
            {
              playerId,
              error: error instanceof Error ? error.message : error,
            }
          );
        });
    }, POST_SUBMIT_POLL_INTERVAL_MS);
    redirectTimerRef.current = setTimeout(() => {
      toast.show({ action: "success", description: "Report saved." });
      router.replace(getExpandedPlayerUrl(`/players/${playerId}`) as Href);
    }, POST_SUBMIT_REDIRECT_MS);
  }, [client, router, getExpandedPlayerUrl, playerId, toast]);

  const handleDictationResult = useCallback(
    (data: Record<string, unknown>, fields: readonly string[]) => {
      console.info(LOG_PREFIX, "Dictation result received:", data, fields);

      if (Platform.OS === "web") {
        // Web: inject values into Form.io iframe via postMessage
        const iframe = iframeRef.current;
        if (iframe?.contentWindow && iframe.src) {
          const targetOrigin = new URL(iframe.src).origin;
          const message = {
            type: "dictation_inject",
            data,
            aiFilledKeys: fields,
          };
          console.info(
            LOG_PREFIX,
            "Posting injection message to iframe:",
            message
          );
          iframe.contentWindow.postMessage(
            JSON.stringify(message),
            targetOrigin
          );
        }
      } else if (webViewRef.current) {
        // Native: inject values into Form.io WebView via injectJavaScript
        const script = buildInjectionScript(data, fields);
        console.info(LOG_PREFIX, "Injecting dictation values into WebView");
        webViewRef.current.injectJavaScript(script);
      }
    },
    []
  );

  const {
    startRecording,
    finishRecording,
    cancelRecording,
    elapsedSeconds,
    liveTranscript,
    audioLevels,
  } = useDictation({
    fieldSchema,
    playerId: playerId ?? "",
    reportTypeId: "",
    onResult: handleDictationResult,
  });

  const handleWebViewMessage = useCallback(
    (event: WebViewMessageEvent) => {
      const parsed = parseWebViewMessage(event);
      // SE-9160: refresh traffic is handled before the log below. The bridge
      // already filters the host's own token-bearing replies; ignoring them
      // here too is the second guard against logging a token.
      const refresh = parsed
        ? bridgeTokenRefreshAction(parsed, {
            formOrigin: originOf(url),
            senderOrigin: originOf(event.nativeEvent.url ?? ""),
          })
        : { kind: "none" as const };
      if (refresh.kind === "answer") {
        console.info(LOG_PREFIX, "Form token refresh requested");
        void answerOnNative(refresh.requestId, refresh.targetOrigin);
        return;
      }
      if (refresh.kind === "ignore") return;
      if (parsed) {
        console.info(LOG_PREFIX, "WebView message:", parsed.type, parsed);
        // Native: the injected bridge relays the renderer's window message as a
        // `formio_message` envelope, so the submit signal arrives nested.
        const inner = parsed.payload as { type?: string } | undefined;
        if (
          parsed.type === REPORT_SUBMITTED_TYPE ||
          (parsed.type === "formio_message" &&
            inner?.type === REPORT_SUBMITTED_TYPE)
        ) {
          handleReportSubmitted();
        }
      } else {
        console.warn(
          LOG_PREFIX,
          "Unparseable WebView message:",
          event.nativeEvent.data
        );
      }
    },
    [handleReportSubmitted, answerOnNative, url]
  );

  const handleWebViewNavigationChange = useCallback(
    (navState: { url?: string; loading?: boolean; title?: string }) => {
      console.info(LOG_PREFIX, "WebView navigation state changed:", {
        url: navState.url?.substring(0, 100),
        loading: navState.loading,
        title: navState.title,
      });
    },
    []
  );

  const handleWebViewError = useCallback(
    (syntheticEvent: { nativeEvent: { description?: string } }) => {
      console.error(
        LOG_PREFIX,
        "WebView error:",
        syntheticEvent.nativeEvent.description
      );
    },
    []
  );

  const handleWebViewLoadEnd = useCallback(() => {
    console.info(LOG_PREFIX, "WebView finished loading form");
  }, []);

  // 2. SIDE EFFECTS

  // Place Dictate button in the navigation header bar.
  // The pr-3 wrapper gives the headerRight element the same right inset the
  // scout-reports screen applies to its header action (SE-5462: without it
  // the outlined button renders flush against the screen edge, visually
  // misaligned with the navigation bar content).
  useEffect(() => {
    if (!isDictationEnabled) return;
    navigation.setOptions({
      headerRight: () => (
        <Box className="pr-3">
          <DictateButton onPress={startRecording} />
        </Box>
      ),
    });
  }, [navigation, isDictationEnabled, startRecording]);

  useEffect(() => {
    console.info(LOG_PREFIX, "Mounting intake form", {
      playerId,
      matchId,
      parsedMatchIds: matchIds,
    });
    // SE-4010: product-analytics mirror of the mount log — an intake form
    // opening for a player IS the "intake started" signal. Ref-guarded so a
    // match re-selection re-running this effect can't emit a second start
    // for the same intake flow.
    if (!playerId || hasLoggedIntakeStartRef.current) return;
    hasLoggedIntakeStartRef.current = true;
    void AnalyticsTracker.scouting.logMatchIntakeStarted({
      player_id: playerId,
      match_count: matchIds.length,
    });
  }, [playerId, matchId, matchIds]);

  useEffect(() => {
    if (error) {
      console.error(LOG_PREFIX, "Failed to fetch form URL:", {
        message: error.message,
        graphQLErrors: error.graphQLErrors?.map(e => e.message),
        networkError:
          error.networkError instanceof Error
            ? error.networkError.message
            : null,
      });
    }
  }, [error]);

  // Fetch Form.io form definition to extract field schema for dictation.
  // URL structure: {renderer}?projectUrl={formioApi}&formId={id}&token={jwt}
  useEffect(() => {
    if (loading || !url || formFetchedRef.current) return;
    formFetchedRef.current = true;

    const parsed = new URL(url);
    const projectUrl = parsed.searchParams.get("projectUrl");
    const formId = parsed.searchParams.get("formId");
    const token = parsed.searchParams.get("token");

    console.info(LOG_PREFIX, "Form URL structure:", {
      origin: parsed.origin,
      pathname: parsed.pathname,
      projectUrl: projectUrl ?? "(missing)",
      formId: formId ?? "(missing)",
      hasToken: Boolean(token),
    });

    if (!projectUrl || !formId) {
      console.warn(
        LOG_PREFIX,
        "Cannot fetch form schema — missing projectUrl or formId in URL params"
      );
      return;
    }

    const formApiUrl = `${projectUrl}/form/${formId}`;

    console.info(LOG_PREFIX, "Fetching form definition from:", formApiUrl);

    fetch(formApiUrl, {
      headers: token ? { "x-jwt-token": token } : {},
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(`${res.status} ${res.statusText}`);
        }
        return res.json() as Promise<{
          components?: FormioComponent[];
          title?: string;
          path?: string;
          name?: string;
        }>;
      })
      .then(formDef => {
        console.info(LOG_PREFIX, "Raw Form.io response:", {
          title: formDef.title,
          path: formDef.path,
          name: formDef.name,
          totalComponents: formDef.components?.length ?? 0,
          topLevelKeys: formDef.components?.map(c => ({
            key: c.key,
            type: c.type,
            label: c.label,
            hasChildren: Boolean(c.components?.length),
            hasColumns: Boolean(c.columns?.length),
          })),
        });

        const fields = extractFormFields(formDef.components ?? []);

        console.info(LOG_PREFIX, "Extracted fields:", {
          dataFieldCount: Object.keys(fields).length,
          fieldKeys: Object.keys(fields),
          fields,
        });

        setFieldSchema(fields);
      })
      .catch(err => {
        console.error(
          LOG_PREFIX,
          "Failed to fetch form definition:",
          err instanceof Error ? err.message : err
        );
      });
  }, [loading, url]);

  // Web: listen for postMessage from Form.io iframe
  useEffect(() => {
    if (Platform.OS !== "web" || !url) return undefined;

    /*
     * The origin the form iframe posts from. Any page can post to this window,
     * and a `report_submitted` message routes the user away and marks the
     * player's tab — so the sender is checked rather than assumed. A form URL
     * with no usable origin — unparseable, or opaque — cannot have produced a
     * loadable form iframe either, so the listener is not installed at all in
     * that case rather than installed against a value that matches anyone.
     */
    const formOrigin = originOf(url);
    if (formOrigin === null) {
      console.warn(
        LOG_PREFIX,
        "Form URL has no usable origin; not listening:",
        url
      );
      return undefined;
    }

    const handleIframeMessage = (event: MessageEvent) => {
      if (event.origin !== formOrigin) return;
      try {
        const parsed =
          typeof event.data === "string" ? JSON.parse(event.data) : event.data;
        const source = parsed?.source ?? "";
        if (
          source === "react-devtools-bridge" ||
          source === "react-devtools-content-script"
        ) {
          return;
        }
        if (isTokenRefreshMessage(parsed)) {
          // SE-9160: answer only the form iframe itself, never another frame
          // that happens to share its origin.
          const requestId = tokenRefreshRequestId(parsed);
          if (requestId && event.source === iframeRef.current?.contentWindow) {
            console.info(LOG_PREFIX, "Form token refresh requested");
            void answerOnWeb(requestId);
          }
          return;
        }
        console.info(LOG_PREFIX, "iframe postMessage received:", parsed);
        if (parsed?.type === REPORT_SUBMITTED_TYPE) {
          handleReportSubmitted();
        }
      } catch {
        // Not JSON
      }
    };

    window.addEventListener("message", handleIframeMessage);
    return () => {
      window.removeEventListener("message", handleIframeMessage);
    };
  }, [url, handleReportSubmitted, answerOnWeb]);

  // Reset dictation state and cancel any pending post-submit redirect on unmount
  useEffect(
    () => () => {
      resetDictationState();
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    },
    []
  );

  // 3. RETURN
  return (
    <MainView
      url={url}
      loading={loading}
      hasError={!!error}
      isDark={isDark}
      dictationStatus={dictationStatus}
      elapsedSeconds={elapsedSeconds}
      liveTranscript={liveTranscript}
      audioLevels={audioLevels}
      processingMessage={processingMessage}
      onCancelRecording={cancelRecording}
      onFinishRecording={finishRecording}
      iframeRef={iframeRef}
      onWebViewMessage={handleWebViewMessage}
      onWebViewNavigationChange={handleWebViewNavigationChange}
      onWebViewError={handleWebViewError}
      onWebViewLoadEnd={handleWebViewLoadEnd}
      webViewRef={webViewRef}
    />
  );
};

export default MainContainer;
